import React, { useEffect, useState } from 'react';
import { Colors } from 'library';
import { Card, Chip, Divider, Icon, Link, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { formatShortWeekdayMonthDay } from 'shared/Dates';
import { Add, CalendarTodayOutlined, ChevronRightRounded, NoteAddOutlined, StarRounded } from '@mui/icons-material';
import { usePostJobNavigation } from 'parent-portal/jobs/postJob/usePostJobNavigation';

import type { DashboardSummary } from '../types';
import { PermissionType, useUserContext } from 'UserContext';

export function PostAJob({
    data,
}: {
    data?: DashboardSummary;
    loading: boolean;
    error: boolean;
    refresh: () => void;
}) {
    const { goToPostJob, goToDraft } = usePostJobNavigation();
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const [randomDraft, setRandomDraft] = useState<DashboardSummary['jobs']['drafts'][0] | undefined>(undefined);
    const { upcoming, drafts } = data?.jobs ?? { upcoming: [], drafts: [] };

    const hasDrafts = drafts.length > 0;
    const upcomingCount = upcoming.length;

    useEffect(() => {
        if (drafts?.length > 0) {
            setRandomDraft(drafts[getRandomIntInclusive(0, drafts.length - 1)]);
        }
    }, [drafts]);

    const randomDraftHeadline = randomDraft?.headline;
    const randomDraftDates =
        randomDraft?.start_date && randomDraft?.end_date
            ? `${formatShortWeekdayMonthDay(randomDraft.start_date)} - ${formatShortWeekdayMonthDay(randomDraft.end_date)}`
            : 'Unknown dates and times';

    const recommendedTeachers = getRandomIntInclusive(1, 3);

    function getRandomIntInclusive(min: number, max: number) {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

    function finishDraft() {
        if (randomDraft) goToDraft('Edit Draft', randomDraft.id);
    }

    function postJob() {
        goToPostJob('Business Dashboard');
    }

    const postJobButton = canPostJobs && (
        <PillButton icon={<Add sx={{ fontSize: 18 }} />} onClick={postJob}>
            Post a Job
        </PillButton>
    );

    return (
        <Card>
            <Stack gap="md">
                {hasDrafts ? (
                    <>
                        <Row justify="between" align="center">
                            <Title size="h2">Unfinished Drafts</Title>
                            {postJobButton}
                        </Row>
                        <Row>
                            <Text weight="bold" tone="accent">
                                You have {drafts.length} draft{drafts.length === 1 ? '' : 's'} to finish before we can
                                start matching.
                            </Text>
                            <Link to="/jobs?initialTab=drafts">See all drafts</Link>
                        </Row>
                        {!!randomDraft && hasPermission(PermissionType.PostJob, randomDraft.address.id) && (
                            <Card padding="sm" tone="muted">
                                <Row justify="between" gap="sm" align="center">
                                    <Stack gap="sm">
                                        <Stack gap="none">
                                            <Text weight="bold">
                                                {randomDraft?.business_job_type === 'SUBSTITUTE'
                                                    ? 'Substitute'
                                                    : 'Trial Run'}{' '}
                                                Position{randomDraftHeadline ? `: ${randomDraftHeadline}` : ''}
                                            </Text>
                                            <Text size="sm" tone="secondary">
                                                {randomDraftDates}
                                            </Text>
                                        </Stack>

                                        <Row>
                                            <Chip
                                                variant="filled"
                                                bgColor={Colors.accentSoft}
                                                textColor={Colors.accent}
                                                size="small"
                                                padding="expanded"
                                            >
                                                <Icon
                                                    icon={StarRounded}
                                                    size="xs"
                                                    bgColor={Colors.white}
                                                    iconColor={Colors.accent}
                                                />
                                                {recommendedTeachers} recommended teacher
                                                {recommendedTeachers === 1 ? '' : 's'} waiting
                                            </Chip>
                                        </Row>
                                    </Stack>
                                    <Link onClick={finishDraft}>
                                        Finish
                                        <ChevronRightRounded fontSize="small" />
                                    </Link>
                                </Row>
                            </Card>
                        )}
                    </>
                ) : (
                    <Stack gap="md" align="center">
                        <Icon
                            icon={NoteAddOutlined}
                            size="lg"
                            bgColor={Colors.secondarySoft}
                            iconColor={Colors.secondary}
                        />
                        <Text size="lg" weight="bold" align="center">
                            Need a substitute? Let&apos;s post a job
                        </Text>
                        <Text tone="secondary" align="center">
                            Tell us the shifts you need covered and we&apos;ll match you with a vetted local teacher.
                        </Text>
                        {postJobButton}
                    </Stack>
                )}
                {upcomingCount > 0 && (
                    <>
                        <Divider />
                        <Row justify="between" align="center">
                            <Row gap="sm" align="center">
                                <CalendarTodayOutlined sx={{ fontSize: 18, color: Colors.textSecondary }} />
                                <Text weight="semibold">
                                    {upcomingCount} upcoming job{upcomingCount === 1 ? '' : 's'} scheduled
                                </Text>
                            </Row>
                            <Link to="/jobs" size="sm" underline={false} color={Colors.secondary}>
                                View all jobs &rarr;
                            </Link>
                        </Row>
                    </>
                )}
            </Stack>
        </Card>
    );
}
